import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, RefreshCw, Trash2, UserPlus, X } from "lucide-react";
import { useRole, useRoleMembers } from "@/api/queries/roles";
import { useUsers } from "@/api/queries/users";
import { useAssignRole, useDeleteRole, useUnassignRole, useUpdateRole } from "@/api/mutations/roles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SectionCard } from "@/components/SectionCard";
import { AuswahlMitSuche, type AuswahlEintrag } from "@/components/AuswahlMitSuche";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { relativeTime } from "@/lib/relativeTime";
import { ApiError } from "@/api/client";

// #274: Rollen-Detail — Mitglieder verwalten. Bei kind="system" ist die Rolle read-only
// (kein Löschen-Button, keine Beschreibungs-Bearbeitung); Mitglieder bleiben verwaltbar.
export function RoleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useRole(id);
  const members = useRoleMembers(id);
  const users = useUsers();

  const [pick, setPick] = useState("");
  const [description, setDescription] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const assign = useAssignRole();
  const unassign = useUnassignRole();
  const update = useUpdateRole();
  const remove = useDeleteRole();

  const errorMessage =
    error instanceof ApiError && error.status === 404
      ? `Rolle "${id}" nicht gefunden`
      : (error as Error | undefined)?.message;

  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Rolle…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/roles" label="Rollen" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const isSystem = data.kind === "system";
  const rows = Array.isArray(members.data) ? members.data : [];
  // ⚠ #331: „schon Mitglied" ist wieder eine einfache Aussage — eine Rolle ist eine GLOBALE
  // Menge, jede Person steht darin höchstens einmal.
  const belegt = new Set(rows.map((m) => m.userId));
  const candidates = (Array.isArray(users.data) ? users.data : []).filter((u) => !belegt.has(u.id));

  // #330 (Nachzug 2): die Auswahl spricht KLARNAMEN — die E-Mail benennt den Menschen.
  // ⚠ Der Gitea-Name stand hier einmal als Zusatz: er ist aber der GENERIERTE Kontoname
  // (`owner-<id>`), also nur eine zweite Kennung in Verkleidung — genau das, was die Regel
  // „Auswahl statt Tippen" abschaffen soll (Nachzug-3-Lehre).
  const userEintraege: AuswahlEintrag[] = candidates.map((u) => ({ id: u.id, primaer: u.email }));
  const gewaehlterUser = candidates.find((u) => u.id === pick);

  function handleAssign() {
    if (!pick || !id) return;
    assign.mutate(
      { roleId: id, userId: pick },
      {
        onSuccess: () => {
          toast.success("Rolle zugewiesen");
          setPick("");
        },
        onError: (e) => toast.error(`Zuweisen fehlgeschlagen: ${(e as Error).message}`),
      },
    );
  }

  function handleDelete() {
    if (!id) return;
    setDeleteError(null);
    remove.mutate(id, {
      onSuccess: () => {
        toast.success("Rolle gelöscht");
        setDeleteOpen(false);
        navigate("/roles", { replace: true });
      },
      onError: (e) => {
        const status = e instanceof ApiError ? e.status : 0;
        setDeleteError(
          status === 409
            ? "Die Rolle hat noch Mitglieder — bitte zuerst alle Mitglieder entfernen."
            : (e as Error).message,
        );
      },
    });
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/roles"
        backLabel="Rollen"
        icon={BadgeCheck}
        title={data.name}
        badge={<Badge variant={isSystem ? "default" : "active"}>{isSystem ? "System-Rolle" : "Fach-Rolle"}</Badge>}
        sub={data.description}
        actions={
          isSystem ? undefined : (
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="role-action-delete">
              <Trash2 className="h-4 w-4" />
              Löschen
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <div className="space-y-[18px]">
          <SectionCard
            title={`Mitglieder (${rows.length})`}
            description="Wer trägt diese Rolle? Zuweisen und Entziehen wirkt sofort."
            noPad
          >
            {/* #330 (Nachzug 2): die Person wird AUSGEWÄHLT, nicht aus einem Dropdown
                gefischt — mit Suche über E-Mail und Gitea-Name.
                ⚠ #331: ein Geltungsbereich wird hier NICHT mehr gewählt (ersatzlos
                gestrichen). Wer eine Vertretung für einen einzelnen Vorgang braucht, wählt
                die Person im Setup der Einrichtung. */}
            <div className="space-y-3 border-b border-border p-[18px]">
              <div className="flex flex-wrap items-start gap-6">
                <div className="space-y-1.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-text3">Person</div>
                  {gewaehlterUser ? (
                    <div
                      className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5"
                      data-testid="role-member-chip"
                    >
                      <span className="text-[12.5px] text-text">{gewaehlterUser.email}</span>
                      <button
                        className="text-text3 hover:text-danger"
                        title="Auswahl aufheben"
                        onClick={() => setPick("")}
                        data-testid="role-member-clear"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <AuswahlMitSuche
                      testId="role-member"
                      oeffnenLabel="User wählen…"
                      suchePlatzhalter="E-Mail suchen…"
                      eintraege={userEintraege}
                      leerText={
                        users.isError
                          ? "Die Benutzer sind gerade nicht abrufbar."
                          : "Alle Benutzer tragen diese Rolle bereits."
                      }
                      onWaehlen={setPick}
                    />
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-transparent">.</div>
                  <Button size="sm" onClick={handleAssign} disabled={!pick || assign.isPending} data-testid="role-assign">
                    <UserPlus className="h-4 w-4" />
                    Zuweisen
                  </Button>
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="p-[18px] text-[12.5px] text-text3">Noch keine Mitglieder.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Zugewiesen</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* ⚠ #331: eine Rolle ist eine GLOBALE Menge — jede Person steht darin
                      höchstens einmal, die Kennung genügt als Schlüssel. */}
                  {rows.map((m) => (
                    <TableRow key={m.userId}>
                      <TableCell>
                        <div className="font-semibold text-text">{m.email ?? m.userId}</div>
                        <div className="mono text-[11px] text-text3">{m.userId}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-[12.5px] text-text2">{relativeTime(m.assignedAt)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={unassign.isPending}
                          onClick={() =>
                            id &&
                            unassign.mutate(
                              { roleId: id, userId: m.userId },
                              {
                                onSuccess: () => toast.success("Rolle entzogen"),
                                onError: (e) => toast.error(`Entziehen fehlgeschlagen: ${(e as Error).message}`),
                              },
                            )
                          }
                          data-testid={`role-unassign-${m.userId}`}
                        >
                          <X className="h-4 w-4" />
                          Entziehen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>

          {!isSystem && (
            <SectionCard title="Beschreibung" description="Nur die Beschreibung ist änderbar — der Name ist dauerhaft.">
              <div className="space-y-3">
                <Textarea
                  rows={3}
                  value={description ?? data.description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="role-description-input"
                />
                <Button
                  size="sm"
                  disabled={description === null || description === data.description || update.isPending}
                  onClick={() =>
                    id &&
                    description !== null &&
                    update.mutate(
                      { id, description },
                      {
                        onSuccess: () => {
                          toast.success("Beschreibung gespeichert");
                          setDescription(null);
                        },
                        onError: (e) => toast.error(`Speichern fehlgeschlagen: ${(e as Error).message}`),
                      },
                    )
                  }
                  data-testid="role-description-save"
                >
                  Speichern
                </Button>
              </div>
            </SectionCard>
          )}
        </div>

        <PropGrid
          rows={[
            ["ID", <span className="mono">{data.id}</span>],
            ["Art", isSystem ? "System-Rolle (kuratiert)" : "Fach-Rolle"],
            ["Mitglieder", String(data.memberCount)],
            ["Angelegt von", data.createdBy ? <span className="mono text-xs">{data.createdBy}</span> : <span className="text-text3">System</span>],
            ["Erstellt", <span className="text-text2">{relativeTime(data.createdAt)}</span>],
          ]}
        />
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Fach-Rolle löschen"
        description="Die Rolle verschwindet aus dem Autorisierungs-Graphen. Prozesse, die sie referenzieren, finden sie danach nicht mehr."
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={remove.isPending}
        errorMessage={deleteError ?? undefined}
        requiredTypeText={data.name}
        onConfirm={handleDelete}
      />
    </div>
  );
}
